import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { videoThumbnails, videoSources } from '@/mocks/salonData';

interface VideoItem {
  id: number;
  title: string;
  duration: string;
  thumbnail: string;
  videoUrl: string;
}

function VideoModal({
  isOpen,
  onClose,
  item,
}: {
  isOpen: boolean;
  onClose: () => void;
  item: VideoItem | null;
}) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Autoplay video when modal opens
      const timer = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(() => {
            // Autoplay blocked, user can click play
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      document.body.style.overflow = '';
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const idx = item.id - 1;
  const detail = {
    description: t(`videoDetails.items.${idx}.description`),
  };

  const askMsg = t('videoDetails.askMessage', { title: item.title });
  const message = encodeURIComponent(askMsg);

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-[100] flex items-center justify-center animate-fade-in"
      onClick={(e) => {
        if (e.target === modalRef.current) onClose();
      }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative z-10 bg-white rounded-2xl overflow-hidden max-w-sm md:max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto animate-scale-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Real Video Player */}
        <div className="relative aspect-[9/16] bg-dark-950 flex items-center justify-center">
          <video
            ref={videoRef}
            src={item.videoUrl}
            poster={item.thumbnail}
            controls
            playsInline
            muted
            loop
            className="w-full h-full object-cover"
            preload="metadata"
          />
        </div>

        {/* Detay Kartı İçeriği */}
        <div className="p-5 md:p-7 space-y-5">
          {/* İşlem Detayları */}
          <div>
            <p className="text-dark-500 text-sm md:text-base leading-relaxed whitespace-pre-line">{detail.description}</p>
          </div>

          {/* WhatsApp CTA */}
          <a
            href={`https://wa.me/905313459987?text=${message}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-champagne-500 text-dark-950 py-3.5 rounded-full font-semibold text-sm md:text-base hover:bg-champagne-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg" />
            {t('videos.askWhatsApp')}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function VideoGallery() {
  const { t } = useTranslation();
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const videoItems: VideoItem[] = Array.from({ length: 15 }, (_, i) => ({
    id: i + 1,
    title: t(`videos.items.${i}.title`),
    duration: t(`videos.items.${i}.duration`),
    thumbnail: videoThumbnails[i + 1],
    videoUrl: videoSources[i + 1],
  }));

  const openModal = (item: VideoItem) => {
    setActiveVideo(item);
    setIsModalOpen(true);
  };

  return (
    <section id="videos" className="py-16 md:py-24 bg-cream-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-champagne-600 text-xs font-medium tracking-[0.2em] uppercase mb-3">
            {t('videos.label')}
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-dark-950 mb-3">
            {t('videos.title')}
          </h2>
          <p className="text-dark-500 text-base md:text-lg max-w-xl mx-auto">
            {t('videos.subtitle')}
          </p>
        </div>

        <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
          {videoItems.map((item) => (
            <button
              key={item.id}
              onClick={() => openModal(item)}
              className="flex-shrink-0 w-[260px] md:w-[320px] snap-center group cursor-pointer text-left"
            >
              <div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-dark-100">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30 group-hover:bg-white/30 transition-all">
                    <i className="ri-play-fill text-2xl md:text-3xl text-white ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-md">
                  {item.duration}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <VideoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        item={activeVideo}
      />
    </section>
  );
}